import { Alert } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { LoadType } from 'types/domainTypes';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { NumericTextField } from '../../components/FormFields/NumericTextField';
import { ReactElement } from 'react';

const getLabelText = (loadType: LoadType): string | undefined => {
  switch (loadType) {
    case LoadType.PermanentLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-permanent-load');
    case LoadType.SnowLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-snow-load');
    case LoadType.PressureLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-pressure-load');
    case LoadType.SuctionLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-suction-load');
    case LoadType.ImposedLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-imposed-load');
    case LoadType.ConstructionLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-construction-load');
    case LoadType.ExcepSnowLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.label-excep-snow-load');
    default:
      return;
  }
};

export type UniformLoadFormData = {
  uniformLoad: number | '';
};

interface UniformLoadDialogProps {
  uniformLoad?: number;
  title: string;
  loadType: LoadType;
  onClose: () => void;
  onSave: (formData: UniformLoadFormData) => void;
}

export function UniformLoadDialog({ uniformLoad, title, loadType, onClose, onSave }: UniformLoadDialogProps): ReactElement {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<UniformLoadFormData>({ reValidateMode: 'onChange', defaultValues: { uniformLoad: uniformLoad || '' } });

  const submitHandler = (formData: UniformLoadFormData) => {
    if (loadType === LoadType.SuctionLoad) {
      formData.uniformLoad = -Math.abs(formData.uniformLoad as number);
    }

    onSave(formData);
  };

  return (
    <EditDialog
      title={title}
      onClose={onClose}
      handleFormSubmit={handleSubmit(submitHandler)}
      isDirty={isDirty}
      saveActionText={t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.save-action-text')}
    >
      <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.alert')}</Alert>
      {loadType === LoadType.PermanentLoad && (
        <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.removable-load-alert')}</Alert>
      )}
      <NumericTextField
        data-testid={'uniform-load-test-id'}
        name="uniformLoad"
        required
        control={control}
        label={getLabelText(loadType)}
        fullWidth
        adornmentProps={{ endAdornment: 'kN/m²' }}
        decimalPlaces={3}
        allowNegative={loadType === LoadType.SuctionLoad}
        requiredTextAdornment={
          loadType === LoadType.SuctionLoad
            ? t('calculation-page.spans-and-loads-dialog.uniform-load-dialog.required-text-adornment')
            : undefined
        }
      />
    </EditDialog>
  );
}
